/*

    e n v i r o n m e n t
    Environment

    :description:
    A file to hold JS code that can help us to understand the environment that we're running in,
    ie is it mobile/desktop, ios/windows, etc.

    In our other code modules we've just got to handle decisions based on the information we're getting
    from this file. That way we'll easily be able to run the code in any environment by passing values
    to the constructor of environment.

*/

//
//  :statics:
import { isIOSSixteenOrGreater } from './ios'
import {
  ENUM_MODES,
  ENUM_MODE_MOBILE,
  ENUM_MODE_DESKTOP,
  ENUM_PLATFORMS,
  ENUM_PLATFORM_ANDROID,
  ENUM_PLATFORM_IOS,
  ENUM_PLATFORM_WINDOWS,
  ENUM_PLATFORM_LINUX,
  ENUM_APP_USER_AGENT_SIGNATURES,
} from './statics'

export class Environment {
  constructor(mode, platform, canHandleAR) {
    //
    //  Check for empty strings.
    if (mode === '' || platform === '') {
      throw new Error(`error, empty strings are not valid as inputs, mode: '${mode}', platform: '${platform}'`)
    }
    //
    //  The caller can provide any value here, check before continuing.
    if (mode && ENUM_MODES.indexOf(mode) === -1) {
      throw new Error(`error, mode '${mode}' is not one the required values: '${ENUM_MODES}'`)
    }
    if (platform && ENUM_PLATFORMS.indexOf(platform) === -1) {
      throw new Error(`error, platform '${platform}' is not one the required values: '${ENUM_PLATFORMS}'`)
    }
    //
    //  Set out all of our defaults.
    this.mode = mode || null
    this.platform = platform || null
    this.canHandleAR = canHandleAR || null
    this.hasBeenScanned = false
    this.webGLRenderer = null
    this.skipARScan = false
    //
    //  See if we need to mark this object as already "scanned".
    if (mode && platform && (canHandleAR === false || canHandleAR === true)) {
      this.hasBeenScanned = true
    }
    //
    //  :runtime:
    this.scanning = false
    this.canvas = null
    this.rendererInfo = null
  }

  setSkipARScans() {
    this.skipARScan = true
  }

  resetHasBeenScanned() {
    this.hasBeenScanned = false
  }

  async scan() {
    /*
        A function to "scan" the environment that we're running in, it will set our values for
        platform, mode, and canHandleAR.
    */
    //
    //  If we are already scanning, use basic recursion to await the result.
    if (this.scanning) {
      await new Promise(r => setTimeout(r, 75))
      return this.scan()
    }
    //
    //  Do not re-scan if we've already scanned.
    if (this.hasBeenScanned) {
      return false
    }
    //console.error(`now running scan runtime`)
    //
    //  Set that we are now scanning.
    this.scanning = true
    //
    //  We can read information about our webgl renderer directly, do that first.
    this.webGLRenderer = this.readWebGLRendererType()
    //if (!this.readWebGLRendererType) {
    //  console.warn("caution, unable to read webGLRendererType");
    //}
    //
    //   Ok, we can look at is the user agent, and try to set our platform from it.
    if (navigator) {
      //
      //  Ensure that we can read the userAgent from navigator.
      if (typeof navigator.userAgent === 'string') {
        //
        //  Note, here it's also possible to use "navigator.platform".
        const platform = this.readPlatformFromUserAgent(navigator.userAgent)
        if (ENUM_PLATFORMS.indexOf(platform) > -1) {
          this.platform = platform
        } else {
          this.scanning = false
          throw new Error(`error, unexpected platform '${platform}'`)
        }
      }
    }
    //
    //  We can easily now set our mode from our platform.
    const mode = this.readMode()
    if (!mode) {
      throw new Error('error, unable to set our mode')
    }
    this.mode = mode
    //
    //  Ok, now we can set our canHandleAR value, but not if we've been flagged to skip and only if it's not
    //  already set.
    if (this.mode === ENUM_MODE_MOBILE && this.canHandleAR === null && !this.skipARScan) {
      if (this.platform === ENUM_PLATFORM_ANDROID) {
        this.canHandleAR = await this.readCanHandleARAndroid()
      }
      if (this.platform === ENUM_PLATFORM_IOS) {
        this.canHandleAR = await this.readCanHandleARApple()
      }
    } else {
      this.canHandleAR = false
    }
    //
    //  Update that we've scanned this environment.
    this.hasBeenScanned = true
    this.scanning = false
    return true
    /*
    console.warn(
      "After Environment.scan() -> ",
      JSON.stringify(this.serialise())
    );
    */
    //
    //  Fallback code/potential for integration.
    // var isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    // || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  }

  serialise() {
    return {
      mode: this.mode,
      platform: this.platform,
      canHandleAR: this.canHandleAR,
      hasBeenScanned: this.hasBeenScanned,
    }
  }

  readMode() {
    //
    //  Caution, requires there to be a platform.
    if (!this.platform) {
      throw new Error('error, unable to read the mode as platform is not set')
    }
    if (this.platform === ENUM_PLATFORM_WINDOWS || this.platform === ENUM_PLATFORM_LINUX) {
      return ENUM_MODE_DESKTOP
    }
    if (this.platform === ENUM_PLATFORM_ANDROID) {
      return ENUM_MODE_MOBILE
    }
    //
    //  ios, we can check for iphone/ipad in the user agent.
    if (navigator) {
      if (navigator.userAgent) {
        const matched = navigator.userAgent.match(/iphone|ipad/gi)
        if (matched) {
          return ENUM_MODE_MOBILE
        }
      }
    }
    //
    //  Which kind of iOS device do we have?
    //  @Louis: iPads don't work here yet, TODO: Repair this.
    if (this.platform === ENUM_PLATFORM_IOS) {
      //
      //  New Apple devices.
      if (navigator) {
        if (typeof navigator.maxTouchPoints === 'number') {
          //
          //  Will likely fail due to ios touch screen monitors.
          if (navigator.maxTouchPoints > 1) {
            return ENUM_MODE_MOBILE
          }
        }
      }
      //
      //  All other ios devices must be desktop.
      return ENUM_MODE_DESKTOP
    }
    //
    //  If we get to here, we were unable to read our mode.
    return null
  }

  readCanHandleARApple() {
    return new Promise(resolve => {
      //
      //  If we don't have knowledge of a renderer, we can't continue here.

      if (!this.webGLRenderer) {
        resolve(false)
        return
      }

      //
      //  2022-11-08: Chrome bug on iOS, relList is no longer working. All iOS 16 devices support AR.
      if (isIOSSixteenOrGreater()) {
        resolve(true)
        return
      }

      //
      //  Now check via the modern way, resolves to true/false (should be ok).
      try {
        const result = document.createElement('a').relList.supports('ar')
        if (result === true) {
          resolve(true)
          return
        }
      } catch (err) {
        // eslint no-empty
      }

      //
      //  Now modern apple devices.
      const modernAppleDeviceRegex = /Apple A(\d+)/gi
      const matchesModernAppleDevice = modernAppleDeviceRegex.exec(this.webGlRendererInformation)
      if (matchesModernAppleDevice && matchesModernAppleDevice.length === 2) {
        let appleGPUVersion = matchesModernAppleDevice[1]
        appleGPUVersion = parseInt(appleGPUVersion)
        if (appleGPUVersion >= 9) {
          resolve(true)
          return
        }
      }

      //
      //  @Louis: Is the following block even required?
      //  Some of the apple devices can only be confirmed as non-ar using their webgl render information.
      const ancientAppleDeviceRegex = /PowerVR SGX (.*)/gi
      //
      //  Check for ancient apple device first.
      const matchesAncientAppleDevice = ancientAppleDeviceRegex.exec(this.webGlRendererInformation)
      if (matchesAncientAppleDevice && matchesAncientAppleDevice.length === 2) {
        //console.error("caution, ancient apple device");
        resolve(false)
        return
      }

      resolve(false)
    })
  }

  readCanHandleARAndroid() {
    return new Promise(async resolve => {
      //
      //  Checking for negatives first.
      if (!navigator) {
        resolve(false)
        return
      }
      if (!navigator.xr) {
        resolve(false)
        return
      }
      if (!navigator.xr.isSessionSupported) {
        resolve(false)
        return
      }
      //
      //  Now we can query navigator directly.
      const arIsSupported = await navigator.xr.isSessionSupported('immersive-ar')
      resolve(arIsSupported)
      return
    })
  }

  _readPlatformBlockFromUserAgent(callerUserAgent) {
    //
    //    Note, here we may be able to use navigator.platform, but it has a chance to not be set ("").
    //
    //  Use the useragent that's available by default if we weren't given one.
    let userAgent = callerUserAgent
    if (typeof userAgent !== 'string') {
      userAgent = navigator.userAgent || null
      //
      //  Add a default to "windows" platform.
      if (userAgent) {
        return 'windows'
      }
    }
    //
    //  Note, User Agents are not predicable! We'll try our best with regexes.
    const platformRegex = /\((.*?)\)/g
    const platformMatches = platformRegex.exec(userAgent)
    //
    //  If we didn't get any matches (there should always be 2), just return.
    if (!platformMatches || platformMatches.length !== 2) {
      //console.warn(
      //  `warning, unable to read any matches from this user agent '${userAgent}'`
      //);
      return null
    }
    //
    //  Define the place to store the block containing the platform information.
    let platformBlock = platformMatches[1]
    if (!platformBlock) {
      //
      //  If it's falsy we can't continue.
      throw new Error('error, unable to read the platformBlock from userAgent')
    }
    //
    //  Change the "platform" we have in our hands to all lowercase to make matching easier.
    platformBlock = platformBlock.toLowerCase()
    //
    //  Return our platform block.
    return platformBlock
  }

  readPlatformFromUserAgent(userAgent) {
    const platformBlock = this._readPlatformBlockFromUserAgent(userAgent)
    //
    //  We can just use logic to work this out, go through each of our options.
    const matchedPlatforms = {
      ios: false,
      android: false,
      linux: false,
      windows: false,
    }
    //
    //  :ios-ish:
    if (
      platformBlock.indexOf('iphone') > -1 ||
      platformBlock.indexOf('ipad') > -1 ||
      platformBlock.indexOf('macintosh') > -1
    ) {
      matchedPlatforms.ios = true
    }
    //
    //  :android-ish:
    if (platformBlock.indexOf('android') > -1) {
      matchedPlatforms.android = true
    }
    //
    //  :linux-ish:
    if (platformBlock.indexOf('linux') > -1 && !matchedPlatforms.android) {
      //
      //  We know that linux appears on all android platforms, so consult there before marking
      //  it as "linux".
      matchedPlatforms.linux = true
    }
    if (platformBlock.startsWith('x11')) {
      matchedPlatforms.linux = true
    }
    //
    //  :windows-ish:
    if (platformBlock.startsWith('windows')) {
      matchedPlatforms.windows = true
    }
    //
    //  Now look through our results and verify we only have 1 platform.
    let platform = null
    let totalMatches = 0
    for (const [key, value] of Object.entries(matchedPlatforms)) {
      if (value === true) {
        totalMatches += 1
        //
        //  Set our active platform, caution, will always overwrite if there are duplicates.
        platform = key
      }
    }
    //
    //  Make sure that we only read 1 match for the platform.
    if (totalMatches !== 1) {
      console.error(`error, multiple platforms matched for: '${userAgent}' -> '${JSON.stringify(matchedPlatforms)}'`)
      //  TODO: Throw here.
    }
    //
    //  Now return the platform, it's either null, or one of our keys [ios, android, linux, windows].
    return platform
  }

  readWebGLRendererType() {
    //
    //  We just need to create a canvas element, then set a context to it.
    //  https://webcache.googleusercontent.com/search?q=cache:xvRGzbiMtZ0J:https://51degrees.com/Blog/device-detection-for-apple-iphone-and-ipad+&cd=1&hl=en&ct=clnk&gl=uk&client=ubuntu
    //
    //  If we already have a result, return it.
    if (this.rendererInfo) {
      return this.rendererInfo
    }
    //
    //  Create a canvas HTML element.
    const canvas = this.canvas || document.createElement('canvas')
    if (canvas) {
      if (canvas.getContext) {
        const context = canvas.getContext('webgl') || canvas.getContext('experimental-webgl')
        if (context) {
          if (context.getExtension) {
            const rendererInformation = context.getExtension('WEBGL_debug_renderer_info')
            if (rendererInformation) {
              //
              //  Finally, set our webgl render information.
              //  Caution, UNMASKED_RENDERER_WEBGL can be hidden due to security reasons.
              this.canvas = canvas
              this.rendererInfo = context.getParameter(rendererInformation.UNMASKED_RENDERER_WEBGL)
              return this.rendererInfo
            }
          }
        }
      }
    }
    return null
  }

  throwErrorIfEnvironmentHasNotYetBeenScanned() {
    if (!this.hasBeenScanned) {
      throw new Error("error, the environment hasn't been scanned")
    }
  }

  deviceSupportsAbsoluteOrientationSensor() {
    return new Promise((resolve, reject) => {
      let sensor
      try {
        const options = { frequency: 60, referenceFrame: 'device' }
        sensor = new AbsoluteOrientationSensor(options)
        sensor.addEventListener('error', error => {
          resolve(false)
        })
        sensor.addEventListener('activate', error => {
          sensor.stop()
          resolve(true)
        })
        sensor.start()
      } catch (err) {
        resolve(false)
      }
    })
  }

  //
  //  :helpers:
  //  These functions are the ones that should be used by external callers, they provide a
  //  simply interface to get specific information about this environment.
  isDesktop() {
    this.throwErrorIfEnvironmentHasNotYetBeenScanned()
    return this.mode === ENUM_MODE_DESKTOP
  }

  isMobile() {
    this.throwErrorIfEnvironmentHasNotYetBeenScanned()
    return this.mode === ENUM_MODE_MOBILE
  }

  isWindows() {
    this.throwErrorIfEnvironmentHasNotYetBeenScanned()
    return this.platform === ENUM_PLATFORM_WINDOWS
  }

  isAndroid() {
    this.throwErrorIfEnvironmentHasNotYetBeenScanned()
    return this.platform === ENUM_PLATFORM_ANDROID
  }

  isiOS() {
    this.throwErrorIfEnvironmentHasNotYetBeenScanned()
    return this.platform === ENUM_PLATFORM_IOS
  }

  isLinux() {
    this.throwErrorIfEnvironmentHasNotYetBeenScanned()
    return this.platform === ENUM_PLATFORM_LINUX
  }

  isARCompatible() {
    this.throwErrorIfEnvironmentHasNotYetBeenScanned()
    return this.canHandleAR === true
  }

  isInApp() {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera
    let matched = false
    ENUM_APP_USER_AGENT_SIGNATURES.map(signature => {
      if (matched) {
        return
      }
      if (userAgent.match(new RegExp(signature, 'g') !== null)) {
        matched = true
      }
    })
    return matched
  }
}
